import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';
import { jwtDecode } from 'jwt-decode';
import { User, WalletInfo } from '../types';
import { authApi } from '../services/api';
import { toast } from 'react-hot-toast';
import type { WalletInfo as WalletServiceInfo } from '../services/wallet';
import { kycService } from '../services/kyc';
import { stripeService } from '../services/stripe';
import { clearAuthStorage, clearUserScopedStorage } from '../config/authRoutes';
import { pickSsoLogoutUrl, ssoNav, startSsoStepUp } from '../services/ssoApi';
import { exitSignInAs, isImpersonationToken, readImpersonation } from '../services/impersonation';
import config from '../config';

// services/wallet.ts imports ethers. Load it only when a wallet is actually used, so the sign-in bundle does not carry web3.
const loadWalletService = () => import('../services/wallet').then((m) => m.walletService);

// Impersonation state
interface ImpersonationState {
  targetUserId: string;
  targetEmail: string;
  sandboxTenantId: string;
  originalToken: string;
}

/**
 * Sign-in refusals that describe the account, not the password. The server
 * sends each with a sentence that names the next step.
 *
 * These are KNOWN CODES, not a gate. `status` decides that a 403 is an
 * account-state refusal; a code, where the server sends one, refines it.
 * Keying the classification on this allowlist made client truthfulness
 * depend on server-code rollout: a refusal carrying an unlisted code, or no
 * code at all, fell through to "your password is wrong" — the exact collapse
 * `LoginOutcome` exists to remove. The list stays as documentation of what
 * the server can say, and for behaviour that genuinely needs a specific code.
 * It no longer decides whether a 403 is allowed to be honest.
 */
export const ACCOUNT_REFUSAL_CODES = [
  'ACCOUNT_SETUP_INCOMPLETE',
  'ACCOUNT_AUTHENTICATOR_RESET',
  'ACCOUNT_DEACTIVATED',
  'ACCOUNT_MFA_NOT_ENROLLED',
  // Microsoft single sign-on: this account signs in with Microsoft, not a password.
  'SSO_REQUIRED',
];

/**
 * What a sign-in attempt actually meant.
 *
 * This is the semantic boundary for authentication. Before it existed, `login`
 * returned `boolean | 'requires2FA' | 'rateLimited'`, which distinguished three
 * things and collapsed everything else to `false` — so a deactivated account, a
 * half-finished activation, an account awaiting authenticator enrolment, a 500
 * and a dropped connection all arrived at the UI as the same word, and both
 * sign-in surfaces rendered it as "your password is wrong". Five of nine server
 * outcomes were mistold.
 *
 * The union describes MEANING, not transport. Callers branch on `kind` and
 * never see a status code or a machine error token. There are two consumers —
 * `/login` and `/portal/dialer/login` — and they must stay in step: the whole
 * point is that both receive the same authentication truth from one seam.
 *
 * `message` is the server's own reviewed sentence where one exists. Surfaces
 * may substitute their own wording for the outcomes they can phrase better for
 * their audience; shared semantics do not require identical copy.
 *
 * The invariant, in one line: **`status` determines the semantic class, `code`
 * refines it, `message` communicates it.** `code` is therefore optional and
 * additive — it can sharpen behaviour for a caller that wants a particular
 * refusal, and its absence can never make an outcome less truthful.
 */
export type LoginOutcome =
  | { kind: 'success' }
  | { kind: 'requires2FA' }
  | { kind: 'rateLimited'; message: string }
  | { kind: 'invalidCredentials'; message: string }
  | { kind: 'accountState'; message: string; code?: string }
  | { kind: 'serverError'; message: string }
  | { kind: 'offline'; message: string };

/**
 * Translate one API response into what it meant.
 *
 * Pure and exported so the mapping is legible on its own, though the contract
 * is tested through `login` itself rather than here — what matters is that the
 * seam behaves, not that a helper does.
 *
 * `message` is read ONLY from `response.message`. The global error handler puts
 * machine codes in `response.error` (`INTERNAL_ERROR`, `RATE_LIMITED`), so
 * sourcing copy from it would print those tokens to users.
 */
export function loginOutcomeFor(response: {
  success?: boolean;
  data?: unknown;
  message?: string;
  status?: number;
  code?: string;
  requires2FA?: boolean;
}): LoginOutcome {
  const said = typeof response.message === 'string' && response.message.trim() ? response.message : '';

  // A 2FA challenge is a 200 carrying `success: false`, so it must be read
  // before anything keyed on success or status.
  if (response.requires2FA) return { kind: 'requires2FA' };
  if (response.success && response.data) return { kind: 'success' };

  const status = response.status;

  // No response at all: no status, and no body to have carried a code. That
  // absence is the only signal, and it is emphatically not a refusal.
  if (status === undefined) {
    return { kind: 'offline', message: said || 'Could not reach the server.' };
  }
  if (status === 429) {
    return { kind: 'rateLimited', message: said || 'Too many sign-in attempts. Wait and try again.' };
  }
  if (status === 401) {
    return { kind: 'invalidCredentials', message: said || 'Those credentials were not accepted.' };
  }
  if (status === 403) {
    // The server's sentence is the reviewed one here — "This account has not
    // completed setup…", "Account has been deactivated", "This account requires
    // an authenticator app…". Collapsing these into one client-written string
    // would throw away the only actionable thing the user is told.
    //
    // The status is what makes this an account-state refusal. The code comes
    // along when the server sends one (`ACCOUNT_AUTHENTICATOR_RESET` and its
    // siblings, added by the recovery work) so a caller can act on a specific
    // refusal — but it is never consulted to decide the class, so a server
    // that has not shipped codes yet still tells this person the truth.
    const code = typeof response.code === 'string' && response.code.trim() ? response.code : undefined;
    return { kind: 'accountState', message: said || 'This account cannot sign in yet.', code };
  }
  if (status >= 500) {
    return { kind: 'serverError', message: said || 'The server failed while signing you in.' };
  }

  // Anything else is a fault we did not anticipate. Deliberately NOT
  // `invalidCredentials`: defaulting an unknown failure to "wrong password" is
  // the collapse this type exists to remove.
  return { kind: 'serverError', message: said || 'Sign-in did not go through.' };
}

// Enhanced UserContextType with backend-only auth flow
interface EnhancedUserContextType {
  user: User | null;
  wallet: WalletInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  kycStatus: string;
  isStripeConnected: boolean;
  sandboxTenantId: string | null;
  impersonating: ImpersonationState | null;
  login: (email: string, password: string, totpCode?: string) => Promise<LoginOutcome>;
  /** Finish a Microsoft sign-in from the /auth/sso/exchange body (same shape as a password-login success). */
  completeSsoLogin: (responseData: unknown) => Promise<LoginOutcome>;
  register: (data: {
    email: string;
    password: string;
    confirmPassword: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    kyc?: any;
    profile?: any;
  }) => Promise<boolean>;
  logout: () => void;
  connectWallet: () => Promise<boolean>;
  disconnectWallet: () => void;
  initiateKyc: () => Promise<{ success: boolean; kycUrl?: string; error?: string }>;
  checkKycStatus: () => Promise<string>;
  setupStripeConnect: () => Promise<{ success: boolean; setupUrl?: string; error?: string }>;
  checkStripeStatus: () => Promise<boolean>;
  canInvest: () => { canInvest: boolean; reasons: string[]; nextSteps: string[] };
  refreshUser: () => Promise<void>;
  loginAs: (targetUserId: string, targetEmail: string) => Promise<boolean>;
  exitImpersonation: () => void;
}

// Action types for the reducer
type UserAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_WALLET'; payload: WalletInfo | null }
  | { type: 'SET_AUTHENTICATED'; payload: boolean }
  | { type: 'SET_KYC_STATUS'; payload: string }
  | { type: 'SET_STRIPE_CONNECTED'; payload: boolean }
  | { type: 'SET_SANDBOX_TENANT'; payload: string | null }
  | { type: 'SET_IMPERSONATING'; payload: ImpersonationState | null }
  | { type: 'LOGOUT' };

// State interface
interface UserState {
  user: User | null;
  wallet: WalletInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  kycStatus: string;
  isStripeConnected: boolean;
  sandboxTenantId: string | null;
  impersonating: ImpersonationState | null;
}

const initialState: UserState = {
  user: null,
  wallet: null,
  isAuthenticated: false,
  isLoading: true,
  kycStatus: 'not_started',
  isStripeConnected: false,
  sandboxTenantId: null,
  impersonating: null,
};

// Reducer function
function userReducer(state: UserState, action: UserAction): UserState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_USER':
      console.log('🔴 SET_USER REDUCER:', {
        'payload': action.payload,
        'payload.kyc_status': action.payload?.kyc_status,
        'state.kycStatus BEFORE': state.kycStatus,
        'will be set to': action.payload?.kyc_status || state.kycStatus
      });
      const newState = { 
        ...state, 
        user: action.payload,
        isAuthenticated: !!action.payload,
        // CRITICAL FIX: Update kycStatus from user object when setting user
        kycStatus: action.payload?.kyc_status || state.kycStatus
      };
      console.log('🔴 SET_USER REDUCER RESULT:', {
        'newState.kycStatus': newState.kycStatus,
        'newState.user.kyc_status': newState.user?.kyc_status
      });
      return newState;
    case 'SET_WALLET':
      return { ...state, wallet: action.payload };
    case 'SET_AUTHENTICATED':
      return { ...state, isAuthenticated: action.payload };
    case 'SET_KYC_STATUS':
      console.log('🟢 SET_KYC_STATUS REDUCER:', {
        'OLD state.kycStatus': state.kycStatus,
        'NEW payload': action.payload
      });
      return {
        ...state,
        kycStatus: action.payload,
        user: state.user ? { ...state.user, kyc_status: action.payload as User['kyc_status'] } : state.user,
      };
    case 'SET_STRIPE_CONNECTED':
      return { ...state, isStripeConnected: action.payload };
    case 'SET_SANDBOX_TENANT':
      return { ...state, sandboxTenantId: action.payload };
    case 'SET_IMPERSONATING':
      return { ...state, impersonating: action.payload };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        wallet: null,
        isAuthenticated: false,
        kycStatus: 'not_started',
        isStripeConnected: false,
        sandboxTenantId: null,
        impersonating: null,
      };
    default:
      return state;
  }
}

// Create the context
const UserContext = createContext<EnhancedUserContextType | undefined>(undefined);

// Provider props
interface UserProviderProps {
  children: ReactNode;
}

function pickValue(raw: any, camelKey: string, snakeKey: string, fallback?: any) {
  const access = raw?.investorAccess || raw?.investor_access || {};
  return raw?.[camelKey] ?? raw?.[snakeKey] ?? access?.[camelKey] ?? access?.[snakeKey] ?? fallback;
}

function normalizeUser(raw: any): User {
  return {
    id: raw.id,
    email: raw.email,
    role: raw.role || 'user',
    kyc_status: pickValue(raw, 'kycStatus', 'kyc_status', 'not_started'),
    kyc_verified_at: pickValue(raw, 'kycVerifiedAt', 'kyc_verified_at'),
    kyc_session_id: pickValue(raw, 'kycSessionId', 'kyc_session_id'),
    wallet_address: pickValue(raw, 'walletAddress', 'wallet_address'),
    email_verified: pickValue(raw, 'emailVerified', 'email_verified', false),
    two_factor_enabled: pickValue(raw, 'twoFactorEnabled', 'two_factor_enabled', false),
    sandboxTenantId: raw.sandboxTenantId || raw.sandbox_tenant_id || null,
    first_name: pickValue(raw, 'firstName', 'first_name'),
    last_name: pickValue(raw, 'lastName', 'last_name'),
    phone: raw.phone,
    business_name: pickValue(raw, 'businessName', 'business_name'),
    business_address: pickValue(raw, 'businessAddress', 'business_address'),
    address_line1: pickValue(raw, 'addressLine1', 'address_line1'),
    address_line2: pickValue(raw, 'addressLine2', 'address_line2'),
    city: raw.city,
    state: raw.state,
    zip_code: pickValue(raw, 'zipCode', 'zip_code'),
    country: raw.country,
    accreditation_status: pickValue(raw, 'accreditationStatus', 'accreditation_status', 'pending'),
    manual_review_status: pickValue(raw, 'manualReviewStatus', 'manual_review_status', 'pending_review'),
    document_access_status: pickValue(raw, 'documentAccessStatus', 'document_access_status', 'pending_review'),
    investment_eligibility_status: pickValue(raw, 'investmentEligibilityStatus', 'investment_eligibility_status', 'view_only'),
    relationship_source: pickValue(raw, 'relationshipSource', 'relationship_source', null),
    reviewer_id: pickValue(raw, 'reviewerId', 'reviewer_id', null),
    reviewed_at: pickValue(raw, 'reviewedAt', 'reviewed_at', null),
    review_notes: pickValue(raw, 'reviewNotes', 'review_notes', null),
    known_by: pickValue(raw, 'knownBy', 'known_by', null),
    approval_rationale: pickValue(raw, 'approvalRationale', 'approval_rationale', null),
    compliance_caveat: pickValue(raw, 'complianceCaveat', 'compliance_caveat', null),
    missing_compliance_items: pickValue(raw, 'missingComplianceItems', 'missing_compliance_items', []),
    offering_document_access_approved: pickValue(raw, 'offeringDocumentAccessApproved', 'offering_document_access_approved', false),
    investment_execution_approved: pickValue(raw, 'investmentExecutionApproved', 'investment_execution_approved', false),
    created_at: pickValue(raw, 'createdAt', 'created_at', ''),
    updated_at: pickValue(raw, 'updatedAt', 'updated_at', ''),
  };
}

export function UserProvider({ children }: UserProviderProps) {
  const [state, dispatch] = useReducer(userReducer, initialState);

  const delay = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

  // Helper function to update KYC status
  const updateKycStatus = useCallback(async () => {
    if (!config.features.kyc) return;
    try {
      console.log('🔍 Fetching KYC status from backend...');
      const kycResponse = await kycService.getKycStatus();
      
      console.log('📡 KYC Status Response:', {
        success: kycResponse.success,
        data: kycResponse.data,
        timestamp: new Date().toISOString()
      });
      
      if (kycResponse.success && kycResponse.data) {
        const newStatus = kycResponse.data.status;
        console.log('✅ Updating KYC status in context:', {
          newStatus: newStatus,
          timestamp: new Date().toISOString()
        });
        dispatch({ type: 'SET_KYC_STATUS', payload: newStatus });
      }
    } catch (error) {
      console.warn('⚠️ KYC status endpoint not available:', error);
      // Do NOT override to 'verified' — leave the current status from user profile intact
    }
  }, [dispatch]);

  // Helper function to update Stripe Connect status
  const updateStripeStatus = useCallback(async () => {
    try {
      const stripeResponse = await stripeService.getConnectAccountStatus();
      if (stripeResponse.success && stripeResponse.data) {
        dispatch({ 
          type: 'SET_STRIPE_CONNECTED', 
          payload: stripeResponse.data.status === 'enabled'
        });
      }
    } catch (error) {
      console.error('Error getting Stripe status:', error);
    }
  }, [dispatch]);

  // Initialize authentication on app load - Backend only
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        console.log('Initializing backend-only authentication...');
        
        // Check for existing token
        const token = localStorage.getItem('authToken');
        if (token) {
          try {
            // Check if token is expired before making API call
            const decoded: any = jwtDecode(token);
            const expiryTime = decoded.exp * 1000;
            const now = Date.now();

            // Restore sandboxTenantId from stored token
            if (decoded.sandboxTenantId) {
              dispatch({ type: 'SET_SANDBOX_TENANT', payload: decoded.sandboxTenantId });
            }
            // Restore impersonation state marker (impersonatedBy in token)
            if (decoded.impersonatedBy) {
              // Token is an impersonation token — banner will show if impersonating state is set
              // (impersonating state is not persisted across full page refresh by design)
            }
            
            // CRITICAL FIX: Add 5-minute grace period to prevent premature logout
            const GRACE_PERIOD = 5 * 60 * 1000; // 5 minutes
            
            if (expiryTime + GRACE_PERIOD < now) {
              console.log('Stored token is expired beyond grace period, removing...');
              localStorage.removeItem('authToken');
              localStorage.removeItem('tokenExpiry');
              dispatch({ type: 'SET_LOADING', payload: false });
              return;
            }
            
            // Log if we're in grace period (token technically expired but we're keeping it)
            if (expiryTime < now && expiryTime + GRACE_PERIOD >= now) {
              console.log('Token in grace period, attempting refresh...');
            }
            
            // Retry transient failures — but never a refusal.
            //
            // This retried on ANY !success, so a 429 from the rate limiter
            // was hit three more times with backoff, spending the budget that
            // was already exhausted and guaranteeing the lockout it was
            // trying to recover from. A 429 means "stop asking"; a 401 means
            // the token is dead and asking again cannot revive it. Only
            // network faults and 5xx are worth a second attempt.
            const worthRetrying = (r: typeof response) =>
              !r.success && (r.status === undefined || r.status >= 500);

            let response = await authApi.getProfile();
            let retryCount = 0;
            const maxRetries = 2;

            while (worthRetrying(response) && retryCount < maxRetries) {
              console.warn(`getProfile failed (attempt ${retryCount + 1}/${maxRetries + 1}), retrying...`);
              await delay(1000 * Math.pow(2, retryCount));
              response = await authApi.getProfile();
              retryCount++;
            }

            // A rate limit is not a bad token. Dropping it here would sign the
            // person out for being early, and they would sign back in — into
            // the same limiter.
            if (!response.success && response.status === 429) {
              console.warn('getProfile rate limited; keeping the token and stopping.');
              dispatch({ type: 'SET_LOADING', payload: false });
              return;
            }

            if (response.success && response.data) {
              // Normalize user data from backend
              const user = response.data as any;
              const normalizedUser = normalizeUser(user);
              dispatch({ type: 'SET_USER', payload: normalizedUser });
              
              // Store/update token expiry
              localStorage.setItem('tokenExpiry', expiryTime.toString());
              
              // Update KYC status after successful auth (non-blocking)
              updateKycStatus().catch(err => console.warn('KYC status check failed:', err));
              // Stripe is disabled — skip status check
              console.log('✅ User authenticated from stored token');
            } else {
              console.error(`❌ Token validation failed after ${retryCount + 1} attempts, removing token`);
              localStorage.removeItem('authToken');
              localStorage.removeItem('tokenExpiry');
            }
          } catch (error) {
            console.error('Failed to initialize auth with stored token:', error);
            localStorage.removeItem('authToken');
            localStorage.removeItem('tokenExpiry');
          }
        } else {
          console.log('No stored auth token found');
        }
      } catch (error) {
        console.error('Failed to initialize authentication:', error);
      } finally {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    initializeAuth();
  }, [updateKycStatus, updateStripeStatus]);

  // Auto-refresh token before expiry (smart expiry-based refresh)
  useEffect(() => {
    if (!state.isAuthenticated) return;

    const refreshToken = async () => {
      try {
        const response = await authApi.refreshToken();
        if (response.success && response.data?.token) {
          localStorage.setItem('authToken', response.data.token);
          
          // Store expiry time for quick checks
          const decoded: any = jwtDecode(response.data.token);
          const expiryTime = decoded.exp * 1000; // Convert to milliseconds
          localStorage.setItem('tokenExpiry', expiryTime.toString());
          
          console.log('✅ Token refreshed successfully, expires:', new Date(expiryTime));
        } else {
          console.warn('Token refresh returned no token, keeping old token');
        }
      } catch (error) {
        console.error('❌ Failed to refresh token:', error);
        
        // CRITICAL FIX: Don't logout immediately - keep old token and retry
        // This prevents logout on transient network failures
        console.log('Keeping old token, will retry refresh in 1 minute...');
        
        // Schedule retry in 1 minute instead of logging out
        setTimeout(refreshToken, 60 * 1000);
      }
    };

    const token = localStorage.getItem('authToken');
    if (!token) return;
    // A read-only sign-in-as session is never refreshed; it ends on its clock (services/impersonation.ts).
    if (isImpersonationToken(token)) return;

    try {
      // Decode token to get expiry time
      const decoded: any = jwtDecode(token);
      const expiryTime = decoded.exp * 1000; // Convert to milliseconds
      const now = Date.now();
      const timeUntilExpiry = expiryTime - now;

      // If token expires in less than 5 minutes, refresh immediately
      if (timeUntilExpiry < 5 * 60 * 1000) {
        console.log('Token expiring soon, refreshing immediately...');
        refreshToken();
        return;
      }

      // Calculate when to refresh (5 minutes before expiry)
      const refreshTime = timeUntilExpiry - (5 * 60 * 1000);

      console.log('Token refresh scheduled in:', Math.round(refreshTime / 1000 / 60), 'minutes');
      
      // Schedule token refresh
      const timeout = setTimeout(() => {
        console.log('Auto-refreshing token...');
        refreshToken();
      }, refreshTime);

      return () => clearTimeout(timeout);
    } catch (error) {
      console.error('Failed to decode token for refresh scheduling:', error);
      // Fallback to 30-minute interval if decoding fails
      const interval = setInterval(refreshToken, 30 * 60 * 1000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isAuthenticated]);

  // The one place a successful sign-in becomes a session — token storage, expiry and sandbox
  // decode, user state, KYC refresh. The password login and the Microsoft landing route both
  // end here, so the two can never drift apart.
  const finishSignIn = (backendResponse: any): LoginOutcome => {
    // Transform backend response to match frontend User type
    const user = backendResponse?.user;
    const token = backendResponse?.accessToken || backendResponse?.token;
    if (!user || !token) {
      console.error('Sign-in response carried no user or token');
      return { kind: 'serverError', message: 'Sign-in did not go through.' };
    }

    console.log('Backend user data:', user);
    console.log('Token received:', token ? 'Yes' : 'No');

    // Normalize user object to match frontend User type
    const normalizedUser = normalizeUser(user);

    console.log('Normalized user:', normalizedUser);
    console.log('User role:', normalizedUser.role);
    console.log('Is admin?', normalizedUser.role === 'admin');

    // Store token in localStorage
    localStorage.setItem('authToken', token);
    console.log('Token stored in localStorage');

    // Store token expiry + decode sandboxTenantId
    try {
      const decoded: any = jwtDecode(token);
      const expiryTime = decoded.exp * 1000; // Convert to milliseconds
      localStorage.setItem('tokenExpiry', expiryTime.toString());
      console.log('Token expires at:', new Date(expiryTime));
      if (decoded.sandboxTenantId) {
        dispatch({ type: 'SET_SANDBOX_TENANT', payload: decoded.sandboxTenantId });
      }
    } catch (error) {
      console.warn('Failed to decode token for expiry:', error);
    }

    // Update user state FIRST before async operations
    dispatch({ type: 'SET_USER', payload: normalizedUser });
    console.log('User state updated, isAuthenticated should be true');

    // Set loading to false so UI can update
    dispatch({ type: 'SET_LOADING', payload: false });
    console.log('Loading set to false');

    // toast.success(`Welcome back${normalizedUser.role === 'admin' ? ', Admin' : ''}!`);

    // Check and update KYC status (non-blocking)
    updateKycStatus().catch(err => console.warn('KYC status check failed:', err));

    // Stripe is disabled — skip status check

    return { kind: 'success' };
  };

  // Microsoft sign-in: the landing route has traded its one-time code for the same body a
  // password login returns; the session is completed exactly as a password login completes it.
  const completeSsoLogin = async (responseData: unknown): Promise<LoginOutcome> => {
    try {
      return finishSignIn(responseData);
    } catch (error: any) {
      console.error('SSO sign-in error:', error);
      return { kind: 'serverError', message: 'Sign-in did not go through.' };
    }
  };

  // Backend-only login function
  const login = async (email: string, password: string, totpCode?: string): Promise<LoginOutcome> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      console.log('Attempting backend login for:', email);
      
      // Direct backend login only
      const response = await authApi.login({ email, password, ...(totpCode ? { totpCode } : {}) });
      
      console.log('Login API response:', response);

      const rawResponse = response as any;
      const outcome = loginOutcomeFor(rawResponse);

      // Everything that is not a completed sign-in leaves authenticated state
      // untouched and returns what the server actually decided. A rate limit is
      // not a wrong password; an account-state refusal is not a wrong password;
      // an unreachable server is not a wrong password.
      if (outcome.kind !== 'success') {
        if (outcome.kind !== 'requires2FA') console.error('Login failed:', response.message);
        dispatch({ type: 'SET_LOADING', payload: false });
        return outcome;
      }

      if (response.success && response.data) {
        return finishSignIn(response.data);
      }

      // `loginOutcomeFor` already classified this as a success, so reaching here
      // means the body claimed success without a payload. Treat it as a fault
      // rather than as a credential problem.
      console.error('Login failed:', response.message);
      return { kind: 'serverError', message: 'Sign-in did not go through.' };
    } catch (error: any) {
      // apiRequest resolves rather than throwing, so this is a client-side
      // fault (a render or storage failure), not a server answer.
      console.error('Login error:', error);
      return { kind: 'serverError', message: 'Sign-in did not go through.' };
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Backend-only register function
  const register = async (data: {
    email: string;
    password: string;
    confirmPassword: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    businessName?: string;
    businessAddress?: string;
    accreditationStatus?: string;
    accreditationCriteria?: string[];
    kyc?: any;
    profile?: any;
  }): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      console.log('Sending to backend:', { email: data.email, hasPassword: !!data.password });

      // Forward all fields to authApi — it handles cleanup and trimming
      const response = await authApi.register(data);
      
      console.log('Backend registration raw response:', response);
      
      // Backend returns { message, user, token, expiresAt } directly
      // NOT wrapped in { success, data }
      const backendUser = (response as any).user;
      const backendToken = (response as any).token;
      
      if (backendUser && backendToken) {
        // Backend response contains user and token directly
        const user = backendUser;
        const token = backendToken;
        
        console.log('User from backend:', { id: user.id, email: user.email, role: user.role });
        
        // Normalize user object to match frontend User type
        const normalizedUser = normalizeUser({ ...user, emailVerified: false, twoFactorEnabled: false });
        
        // Store token in localStorage
        localStorage.setItem('authToken', token);
        
        // Store token expiry time (CRITICAL FIX for session persistence)
        try {
          const decoded: any = jwtDecode(token);
          const expiryTime = decoded.exp * 1000;
          localStorage.setItem('tokenExpiry', expiryTime.toString());
        } catch (error) {
          console.error('Failed to decode token expiry:', error);
        }
        
        // Update user state
        dispatch({ type: 'SET_USER', payload: normalizedUser });
        
        console.log('✅ Registration successful:', normalizedUser.email);
        return true;
      } else {
        // Check if response indicates an error
        const errorMsg = (response as any).message || (response as any).error || response.error || 'Registration failed';
        console.error('Registration failed:', errorMsg);
        // Surface the backend's reason to the caller, which shows it inline (no toast).
        throw new Error(errorMsg);
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      // Surface a human-readable reason inline (no toast) — the accreditation step renders
      // error.message in its red inline message.
      const isTimeout = error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '');
      const message = isTimeout
        ? 'The request timed out. Please check your connection and try again.'
        : error?.response?.data?.message || error?.message || 'Something went wrong. Please try again.';
      throw new Error(message);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Backend-only logout function
  const logout = async () => {
    // Whose keys to clear, read before the state is reset.
    const signingOut = state.user?.id || null;
    // Set when the server says this session came from Microsoft: after the local clean-up the
    // browser is sent to Microsoft's sign-out so the Microsoft session ends too.
    let ssoLogoutUrl: string | null = null;
    try {
      // Viewing as somebody (read-only): end that first, server-side, so their session is revoked and the
      // administrator's own (re-issued) session is the one this sign-out revokes. Never POST /auth/logout with the
      // viewed person's token: it would end their Arena grants.
      if (readImpersonation()) await exitSignInAs();
      // Call logout API to invalidate token on server
      ssoLogoutUrl = pickSsoLogoutUrl(await authApi.logout());
      
      // Disconnect wallet if connected
      if (state.wallet) {
        await (await loadWalletService()).disconnectWallet();
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local state regardless of API result.
      //
      // One cleanup contract, shared with AuthContext and the transport layer.
      // This used to clear authToken and tokenExpiry only, leaving behind the
      // refreshToken the 401 interceptor reads — and, while impersonating, an
      // admin's original token.
      clearAuthStorage();
      // A deliberate sign-out also clears this person's own unsent note drafts (PHI) on this machine.
      clearUserScopedStorage(signingOut);
      dispatch({ type: 'LOGOUT' });

     // toast.success('Logged out successfully');
      if (ssoLogoutUrl) ssoNav.go(ssoLogoutUrl);
    }
  };

  // Connect wallet function using wallet service
  const connectWallet = async (): Promise<boolean> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const result = await (await loadWalletService()).connectWallet();
      
      if (result.success && result.walletInfo) {
        // Convert wallet service WalletInfo to our WalletInfo type
        const serviceWallet: WalletServiceInfo = result.walletInfo;
        const walletInfo: WalletInfo = {
          address: serviceWallet.address,
          balance: serviceWallet.balance,
          network: serviceWallet.chainName,
          connected: serviceWallet.isConnected,
        };
        
        dispatch({ type: 'SET_WALLET', payload: walletInfo });
        
        // If user is authenticated, sync wallet with backend
        if (state.isAuthenticated) {
          try {
            await authApi.connectWallet(result.walletInfo.address);
            toast.success('Wallet connected successfully!');
          } catch (error) {
            console.error('Failed to sync wallet with backend:', error);
            toast.error('Wallet connected but sync with backend failed');
          }
        } else {
          toast.success('Wallet connected! Please sign in to link it to your account.');
        }
        
        return true;
      } else {
        toast.error(result.error || 'Failed to connect wallet');
        return false;
      }
    } catch (error: any) {
      console.error('Wallet connection error:', error);
      toast.error(error.message || 'Failed to connect wallet');
      return false;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Disconnect wallet function
  const disconnectWallet = async () => {
    try {
      await (await loadWalletService()).disconnectWallet();
      dispatch({ type: 'SET_WALLET', payload: null });
      toast.success('Wallet disconnected');
    } catch (error) {
      console.error('Wallet disconnect error:', error);
    }
  };

  // KYC functions
  const initiateKyc = async (): Promise<{ success: boolean; kycUrl?: string; error?: string }> => {
    if (!config.features.kyc) return { success: false, error: 'Identity verification is not enabled for this organization.' };
    try {
      const response = await kycService.createKycSession();
      if (response.success && response.data) {
        dispatch({ type: 'SET_KYC_STATUS', payload: 'created' });
        return { success: true, kycUrl: response.data.url };
      } else {
        return { success: false, error: response.error || 'Failed to create KYC session' };
      }
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to create KYC session' };
    }
  };

  const checkKycStatus = async (): Promise<string> => {
    if (!config.features.kyc) return 'none';
    try {
      console.log('🔍 checkKycStatus: Fetching status from backend');
      const response = await kycService.getKycStatus();
      
      console.log('📡 checkKycStatus: Response received:', {
        success: response.success,
        status: response.data?.status,
        timestamp: new Date().toISOString()
      });
      
      if (response.success && response.data) {
        const newStatus = response.data.status;
        console.log('✅ checkKycStatus: Updating context with status:', newStatus);
        dispatch({ type: 'SET_KYC_STATUS', payload: newStatus });
        return newStatus;
      }
      console.warn('⚠️ checkKycStatus: No data in response, returning none');
      return 'none';
    } catch (error) {
      console.error('❌ checkKycStatus: Error:', error);
      return 'none';
    }
  };

  // Stripe Connect functions
  const setupStripeConnect = async (): Promise<{ success: boolean; setupUrl?: string; error?: string }> => {
    try {
      const response = await stripeService.createAccountLink();
      if (response.success && response.data) {
        return { success: true, setupUrl: response.data.url };
      } else {
        return { success: false, error: response.error || 'Failed to setup Stripe Connect' };
      }
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to setup Stripe Connect' };
    }
  };

  const checkStripeStatus = async (): Promise<boolean> => {
    try {
      const response = await stripeService.getConnectAccountStatus();
      if (response.success && response.data) {
        const isConnected = response.data.status === 'enabled';
        dispatch({ type: 'SET_STRIPE_CONNECTED', payload: isConnected });
        return isConnected;
      }
      return false;
    } catch (error) {
      console.error('Error checking Stripe status:', error);
      return false;
    }
  };

  // Check user's readiness for investment
  const canInvest = (): { 
    canInvest: boolean; 
    reasons: string[]; 
    nextSteps: string[] 
  } => {
    const reasons: string[] = [];
    const nextSteps: string[] = [];

    console.log('🔍 canInvest: Checking investment eligibility', {
      isAuthenticated: state.isAuthenticated,
      kycStatus: state.kycStatus,
      investmentEligibilityStatus: state.user?.investment_eligibility_status,
      timestamp: new Date().toISOString()
    });

    if (!state.isAuthenticated) {
      console.log('❌ canInvest: Not authenticated');
      reasons.push('Not authenticated');
      nextSteps.push('Sign in or create an account');
    }

    // Sandbox mode: admins bypass KYC gate for testing investor flows
    const isSandboxAdmin = state.user?.role === 'admin' && localStorage.getItem('sandboxPayments') === 'true';

    const eligibilityStatus = state.user?.investment_eligibility_status || 'view_only';
    const investmentApproved = eligibilityStatus === 'eligible_to_invest' || state.user?.investment_execution_approved === true;

    if (!investmentApproved && !isSandboxAdmin) {
      console.log('❌ canInvest: investment eligibility pending', {
        currentStatus: eligibilityStatus,
        kycStatus: state.kycStatus
      });
      reasons.push('Investment requirements pending');
      nextSteps.push('Complete investor verification');
    }

    // NOTE: Stripe Connect and wallet are NOT required for Hive Fund investments
    // Hive Fund uses Stripe Financial Connections (ACH) directly
    // Crypto wallet only needed for token dividend payouts (optional)

    const result = {
      canInvest: reasons.length === 0,
      reasons,
      nextSteps
    };

    console.log('✅ canInvest result:', result);

    return result;
  };

  // Refresh user data
  const refreshUser = async () => {
    try {
      const response = await authApi.getProfile();
      if (response.success && response.data) {
        const user = response.data as any;
        const normalizedUser = normalizeUser(user);
        dispatch({ type: 'SET_USER', payload: normalizedUser });
        // Update KYC status after refreshing user
        await updateKycStatus();
        // Stripe is disabled — skip status check
      }
    } catch (error) {
      console.error('Failed to refresh user data:', error);
    }
  };

  // Impersonate a user (admin only — production safe)
  const loginAs = async (targetUserId: string, targetEmail: string): Promise<boolean> => {
    try {
      const currentToken = localStorage.getItem('authToken');
      if (!currentToken) return false;

      const post = (extra: Record<string, unknown>) => fetch('/api/v1/admin/impersonate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${currentToken}`,
        },
        body: JSON.stringify({ targetUserId, ...extra }),
      });

      let res = await post({});

      if (!res.ok) {
        let err = await res.json().catch(() => ({}));

        // An administrator with an authenticator is asked for a current code, once.
        if (res.status === 401 && err.code === 'STEP_UP_REQUIRED') {
          const entered = window.prompt('Enter the 6-digit code from your authenticator app to view as this user.');
          const totpCode = (entered || '').replace(/\s/g, '');
          if (!/^\d{6}$/.test(totpCode)) {
            if (entered !== null) toast.error('Enter the 6-digit code from your authenticator app.');
            return false;
          }
          res = await post({ totpCode });
          if (!res.ok) err = await res.json().catch(() => ({}));
        }

        if (!res.ok) {
          // A Microsoft-signed-in administrator confirms with Microsoft instead of a code.
          if (err.code === 'STEP_UP_SSO_REQUIRED') {
            if (window.confirm('Viewing as another user needs a fresh confirmation from Microsoft. Continue to Microsoft?')) {
              await startSsoStepUp('IMPERSONATE');
            }
            return false;
          }
          // STEP_UP_FAILED / STEP_UP_UNAVAILABLE and every other refusal: the server's own sentence.
          toast.error(err.message || err.error || 'Impersonation failed');
          return false;
        }
      }

      const data = await res.json();
      const impToken = data.token;
      const decoded: any = jwtDecode(impToken);

      const impersonationState: ImpersonationState = {
        targetUserId,
        targetEmail,
        sandboxTenantId: '',
        originalToken: currentToken,
      };

      // Switch to impersonation token
      localStorage.setItem('authToken', impToken);
      localStorage.setItem('tokenExpiry', (decoded.exp * 1000).toString());
      localStorage.setItem('impersonating_as', targetEmail);
      localStorage.setItem('original_admin_token', currentToken);
      dispatch({ type: 'SET_IMPERSONATING', payload: impersonationState });

      // Fetch impersonated user's profile
      const profileRes = await fetch('/api/v1/auth/me', {
        headers: { Authorization: `Bearer ${impToken}` },
      });
      if (profileRes.ok) {
        const profileData = await profileRes.json();
        const u = profileData.data || profileData.user || profileData;
        const normalizedUser = normalizeUser({ ...u, twoFactorEnabled: false });
        dispatch({ type: 'SET_USER', payload: normalizedUser });
      }

      toast.success(`Now viewing portal as ${targetEmail}`);
      return true;
    } catch (error: any) {
      console.error('loginAs error:', error);
      toast.error('Failed to switch user context');
      return false;
    }
  };

  // Exit impersonation and restore original admin session
  const exitImpersonation = () => {
    // Restore original token — fall back to localStorage if React state was lost
    const originalToken =
      state.impersonating?.originalToken ||
      localStorage.getItem('original_admin_token') ||
      '';
    if (!originalToken) return;

    localStorage.setItem('authToken', originalToken);
    localStorage.removeItem('impersonating_as');
    localStorage.removeItem('original_admin_token');

    // Decode original token to restore expiry + sandboxTenantId
    try {
      const decoded: any = jwtDecode(originalToken);
      localStorage.setItem('tokenExpiry', (decoded.exp * 1000).toString());
      dispatch({ type: 'SET_SANDBOX_TENANT', payload: decoded.sandboxTenantId || null });
    } catch (_) {}

    dispatch({ type: 'SET_IMPERSONATING', payload: null });

    // Reload profile from original token
    fetch('/api/v1/auth/me', {
      headers: { Authorization: `Bearer ${originalToken}` },
    }).then(r => r.json()).then(profileData => {
      const u = profileData.data || profileData.user || profileData;
      const normalizedUser = normalizeUser({ ...u, twoFactorEnabled: false });
      dispatch({ type: 'SET_USER', payload: normalizedUser });
    }).catch(() => null);

    toast.success('Returned to your admin session');
  };

  // Context value object - Backend auth only
  const contextValue: EnhancedUserContextType = {
    user: state.user,
    wallet: state.wallet,
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    kycStatus: state.kycStatus,
    isStripeConnected: state.isStripeConnected,
    sandboxTenantId: state.sandboxTenantId,
    impersonating: state.impersonating,
    login,
    completeSsoLogin,
    register,
    logout,
    connectWallet,
    disconnectWallet,
    initiateKyc,
    checkKycStatus,
    setupStripeConnect,
    checkStripeStatus,
    canInvest,
    refreshUser,
    loginAs,
    exitImpersonation,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
}

// Hook to use the user context
/** The session if one is provided, else undefined — for chrome that can render without one. */
export function useOptionalUser() {
  return useContext(UserContext);
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

// Utility hook for role-based access
export function useRoleAccess() {
  const { user } = useUser();
  
  return {
    isUser: user?.role === 'user',
    isInvestor: user?.role === 'investor',
    isAdmin: user?.role === 'admin',
    hasInvestorAccess: user?.role === 'investor' || user?.role === 'admin',
    hasAdminAccess: user?.role === 'admin',
    isKycVerified: user?.kyc_status === 'verified',
  };
}
